import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  FindInPage as ExtractIcon,
} from '@mui/icons-material';
import { DocumentListItem } from '../../../../../types';

interface DocumentTableProps {
  documents: DocumentListItem[];
  formatFileSize: (bytes: number) => string;
  formatDate: (dateString: string) => string;
  handleDownload: (document: DocumentListItem) => void;
  openUploadDialog: () => void;
  openEditDialog: (document: DocumentListItem) => void;
  openDeleteDialog: (document: DocumentListItem) => void;
}

const DocumentTable: React.FC<DocumentTableProps> = ({
  documents,
  formatFileSize,
  formatDate,
  handleDownload,
  openUploadDialog,
  openEditDialog,
  openDeleteDialog,
}) => {
  return (
    <Paper>
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Document Templates ({documents.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openUploadDialog}
        >
          Upload Document
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>File Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell>Uploaded By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No documents uploaded
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload PDF templates that can be used for document generation
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DocumentIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle2">
                          {document.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {document.fileID}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatFileSize(document.fileSize)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(document.uploadedDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(document.modifiedDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {document.uploadedBy}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleDownload(document)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(document)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(document)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DocumentTable;
